import React from 'react';
import { Link } from 'react-router-dom';
import { ShinyText } from '../ui/shiny-text';

const TermsOfUse: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 text-white">
      {/* Header */}
      <header className="fixed w-full top-0 bg-black/50 backdrop-blur-sm z-50">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-bold">
            <ShinyText>WrapPractice</ShinyText>
          </Link>
          <Link
            to="/"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Content */}
      <div className="pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">
            <ShinyText>Terms of Use</ShinyText>
          </h1>
          
          <div className="bg-gray-900/50 rounded-xl p-8 space-y-8 text-gray-100">
            <div className="text-center text-gray-400 mb-8">
              <p>Last Updated: {new Date().toLocaleDateString()}</p>
            </div>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">1. Acceptance of Terms</h2>
              <p className="mb-4">
                Welcome to WrapPractice, a service provided by Nestsim LLC ("Company," "we," "our," or "us"). 
                These Terms of Use ("Terms") govern your use of the WrapPractice application and website 
                (collectively, the "Service") for practicing Pot Limit Omaha (PLO) wrap identification.
              </p>
              <p className="mb-4">
                By accessing or using our Service, you agree to be bound by these Terms and our Privacy Policy. 
                If you disagree with any part of these terms, you may not access the Service.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and Nestsim LLC.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">2. Description of Service</h2>
              <p className="mb-4">
                WrapPractice is an educational poker training application that provides:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Interactive practice sessions for PLO wrap identification</li>
                <li>Performance tracking and analytics</li>
                <li>Educational content related to poker strategy</li>
                <li>Subscription-based access to premium features</li>
              </ul>
              <p className="mb-4">
                The Service is intended for educational and entertainment purposes only. It does not constitute 
                gambling and involves no real money wagering.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">3. User Accounts and Registration</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-green-400">3.1 Account Creation</h3>
              <p className="mb-4">
                To access our Service, you must create an account using one of our supported third-party authentication providers:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Google OAuth</li>
                <li>Facebook OAuth</li>
                <li>Microsoft OAuth</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-green-400">3.2 Account Responsibilities</h3>
              <p className="mb-4">You are responsible for:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Maintaining the security of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and current information</li>
                <li>Immediately notifying us of any unauthorized use of your account</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-green-400">3.3 Account Eligibility</h3>
              <p className="mb-4">
                You must be at least 18 years old to use our Service. By creating an account, you represent 
                and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">4. Subscription Terms</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-green-400">4.1 Subscription Plans</h3>
              <p className="mb-4">
                We offer subscription-based access to premium features. Current pricing and plan details 
                are displayed on our website and may be updated from time to time.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">4.2 Free Trial</h3>
              <p className="mb-4">
                New users may be eligible for a free trial period. Trial terms include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>2-day free trial for first-time subscribers</li>
                <li>Credit card required for trial activation</li>
                <li>Automatic conversion to paid subscription unless cancelled</li>
                <li>One trial per user account</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-green-400">4.3 Billing and Payment</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Subscriptions are billed monthly in advance</li>
                <li>Payment processing is handled securely by Stripe</li>
                <li>All fees are non-refundable except as required by law</li>
                <li>Prices are subject to change with 30 days' notice</li>
                <li>Failed payments may result in service suspension</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-green-400">4.4 Cancellation</h3>
              <p className="mb-4">
                You may cancel your subscription at any time:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Cancellation can be initiated through your account settings</li>
                <li>Service continues until the end of your current billing period</li>
                <li>No refunds for partial months</li>
                <li>Account data may be retained according to our Privacy Policy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">5. Acceptable Use Policy</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-green-400">5.1 Permitted Uses</h3>
              <p className="mb-4">You may use the Service for:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Personal educational and practice purposes</li>
                <li>Improving your poker skills and knowledge</li>
                <li>Accessing and using features included in your subscription</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-green-400">5.2 Prohibited Uses</h3>
              <p className="mb-4">You may not:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Use the Service for any unlawful purpose or activity</li>
                <li>Attempt to reverse engineer, decompile, or hack the Service</li>
                <li>Share your account credentials with others</li>
                <li>Use automated tools, bots, or scripts to access the Service</li>
                <li>Circumvent any security measures or access controls</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon intellectual property rights</li>
                <li>Transmit malicious code or harmful content</li>
                <li>Create multiple accounts to abuse trial offers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">6. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-green-400">6.1 Our Rights</h3>
              <p className="mb-4">
                The Service and its original content, features, and functionality are owned by Nestsim LLC 
                and are protected by international copyright, trademark, patent, trade secret, and other 
                intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">6.2 License to Use</h3>
              <p className="mb-4">
                We grant you a limited, non-exclusive, non-transferable, revocable license to use the Service 
                in accordance with these Terms for your personal, non-commercial use only.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">6.3 User-Generated Content</h3>
              <p className="mb-4">
                Any feedback, suggestions, or other content you provide to us may be used by us without 
                restriction or compensation to you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">7. Privacy and Data Protection</h2>
              <p className="mb-4">
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect 
                your information when you use the Service. By using the Service, you agree to the collection 
                and use of information in accordance with our Privacy Policy.
              </p>
              <p>
                <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline">
                  View our Privacy Policy
                </Link>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">8. Disclaimers and Limitations</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-green-400">8.1 Educational Purpose</h3>
              <p className="mb-4">
                The Service is provided for educational and entertainment purposes only. Information provided 
                through the Service should not be considered professional advice. Playing poker involves risk, 
                and you should never gamble with money you cannot afford to lose.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">8.2 Service Availability</h3>
              <p className="mb-4">
                We strive to maintain service availability but do not guarantee uninterrupted access. 
                The Service may be temporarily unavailable due to maintenance, updates, or technical issues.
              </p>

              <h3 className="text-xl font-semibold mb-3 text-green-400">8.3 Disclaimer of Warranties</h3>
              <p className="mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">9. Limitation of Liability</h2>
              <p className="mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NESTSIM LLC SHALL NOT BE LIABLE 
                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING 
                BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR IN 
                CONNECTION WITH YOUR USE OF THE SERVICE.
              </p>
              <p className="mb-4">
                OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE 
                SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS 
                PRECEDING THE CLAIM.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">10. Indemnification</h2>
              <p className="mb-4">
                You agree to indemnify, defend, and hold harmless Nestsim LLC and its officers, directors, 
                employees, agents, and affiliates from and against any claims, damages, obligations, losses, 
                liabilities, costs, or debt arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">11. Termination</h2>
              <p className="mb-4">
                We may terminate or suspend your account and access to the Service immediately, without 
                prior notice, for conduct that we believe violates these Terms or is harmful to other users, 
                us, or third parties, or for any other reason in our sole discretion.
              </p>
              <p className="mb-4">
                Upon termination, your right to use the Service will cease immediately. Provisions that by 
                their nature should survive termination shall survive, including ownership provisions, 
                warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">12. Governing Law and Dispute Resolution</h2>
              <p className="mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
                where Nestsim LLC is incorporated, without regard to conflict of law principles.
              </p>
              <p className="mb-4">
                Any disputes arising out of or related to these Terms or the Service shall be resolved through 
                binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">13. Changes to Terms</h2>
              <p className="mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of any material 
                changes by posting the updated Terms on our website and updating the "Last Updated" date.
              </p>
              <p className="mb-4">
                Your continued use of the Service after any changes constitutes acceptance of the new Terms. 
                If you do not agree to the updated Terms, you must stop using the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">14. Severability</h2>
              <p className="mb-4">
                If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining 
                provisions will remain in effect. The invalid or unenforceable provision will be replaced with 
                a valid provision that most closely matches the intent of the original provision.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">15. Entire Agreement</h2>
              <p className="mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and 
                Nestsim LLC regarding the use of the Service and supersede all prior agreements and understandings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-blue-400">16. Contact Information</h2>
              <p className="mb-4">
                If you have any questions about these Terms of Use, please contact us:
              </p>
              <div className="bg-gray-800 p-4 rounded-lg">
                <p><strong>Nestsim LLC</strong></p>
                <p>Support: <a href="https://embed.tawk.to/68430f9adbadee190ffc2bc0/1it2v9ss4" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Live Chat Support</a></p>
                <p>Website: <Link to="/" className="text-blue-400 hover:text-blue-300">wrappractice.nestsim.com</Link></p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black/50 py-8">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <div className="flex items-center justify-center gap-3">
            <img src="/images/NestSim_logo_128px.png" alt="Nestsim LLC Logo" />
            <p>&copy; {new Date().getFullYear()} Nestsim LLC™. All rights reserved.</p>
          </div>
          <div className="mt-4 space-x-4">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="text-gray-400 hover:text-white transition-colors">
              Terms of Use
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfUse;